// Shared projectAd / ads-detail / ad-list → ExpansionAd adapter.
//
// Every ad-preview surface (Product Ads, Campaigns, Campaign Detail,
// UGC Ads, agent ResourceCard, resolveAndOpenMaster) runs this same
// function so AdThumbnail / AdDetailModal always see one shape —
// including `phase` / `failure`, which older per-page adapters dropped
// and which gate the Override QC control.
//
// Passthrough-first: when the payload is already ExpansionAd-shaped
// (GET /api/catalog/:id/ads-detail, GET /api/campaigns/:id/ads-detail),
// prefer the already-flat field so this is a no-op on those surfaces.
// Fall back to projectAd / ad-list aliases (`id`/`_id`, nested `copy`,
// `renderedAt`/`createdAt`) for GET /api/ads and the agent list.
// Never fabricate `failure` — only forward what the API actually sent.

import type { ExpansionAd, RegenerationHistoryEntry } from '../pages/ProductAds';
import { toSceneReelSummary } from './sceneReel';

export function toExpansionAd(a: any): ExpansionAd {
  return {
    adId:           String(a.adId ?? a.id ?? a._id ?? ''),
    productId:      a.productId ?? null,
    campaignId:     a.campaignId ?? null,
    template:       a.template ?? '',
    aspectRatio:    a.aspectRatio ?? '1:1',
    platformFormat: a.platformFormat ?? null,
    kind:           a.kind === 'video' ? 'video' : 'image',
    funnelStage:    a.funnelStage ?? null,
    isMaster:       !!a.isMaster,
    productUrl:     a.productUrl ?? null,
    sourceFileType: a.sourceFileType ?? null,
    status:         a.status ?? 'draft',
    renderStage:    a.renderStage ?? null,
    renderStageAt:  a.renderStageAt ?? null,
    approved:       !!a.approved,
    approvedAt:     a.approvedAt ?? null,
    renderUrl:      a.renderUrl ?? null,
    photorealUrl:   a.photorealUrl ?? null,
    useImageRefAsProduction: !!a.useImageRefAsProduction,
    posterUrl:      a.posterUrl ?? null,
    previewVideoUrl: a.previewVideoUrl ?? null,
    previewImageUrl: a.previewImageUrl ?? null,
    headline:       a.headline ?? a.copy?.headline ?? null,
    ctaText:        a.ctaText ?? a.copy?.cta_text ?? null,
    generatedAt:    a.generatedAt ?? a.renderedAt ?? a.createdAt ?? null,
    metaSyncStatus: a.metaSyncStatus ?? null,
    metaAdId:       a.metaAdId ?? null,
    metaAdsetId:    a.metaAdsetId ?? null,
    regenerating:   !!a.regenerating,
    regenerationStage: a.regenerationStage ?? null,
    regenerationHistory: Array.isArray(a.regenerationHistory)
      ? a.regenerationHistory as RegenerationHistoryEntry[]
      : [],
    variantKind:    a.variantKind ?? null,
    mediaId:        a.mediaId ?? null,
    sourceMedia:    a.sourceMedia ?? null,
    renderErrorMessage: a.renderErrorMessage ?? null,
    visionQc:       a.visionQc ?? null,
    phase:          a.phase ?? null,
    ...(a.failure ? { failure: a.failure } : {}),
    // Projection from QC-1 summarizeVisionQc — never fabricate true.
    qcAdvisoryOutline: typeof a.qcAdvisoryOutline === 'boolean'
      ? a.qcAdvisoryOutline
      : undefined,
    qcOverridden:       !!a.qcOverridden,
    qcOverriddenAt:     a.qcOverriddenAt ?? null,
    qcOverriddenBy:     a.qcOverriddenBy ?? null,
    qcOverrideReason:   a.qcOverrideReason ?? null,
    masterAdId:           a.masterAdId ?? null,
    masterThumbnailUrl:   a.masterThumbnailUrl ?? null,
    masterStatus:         a.masterStatus ?? null,
    masterPlatformFormat: a.masterPlatformFormat ?? null,
    derivativeCount:      a.derivativeCount ?? null,
    // Scene reel (API contract §4). Absent on non-scene ads → null, so
    // every surface keyed on it renders exactly as before.
    renderRoute:          a.renderRoute ?? null,
    sceneReel:            toSceneReelSummary(a.sceneReel)
  };
}
